import { PreventSearchKeywords, PreventSearchKeywordsAlias } from 'constants/data/search';
import { HTTP_STATUS } from 'constants/Enums/http';
import { IS_ENCRYPTED_PRICE } from 'sysconfig';
import { sanitize } from 'utils/ObjectUtils';
import { changeAlias } from 'utils/StringUtils';
import { GET, getData, GET_ALL, POST } from './Clients';
import { PRODUCT_TAG } from 'constants/Tags';

const FRONTEND_APIS_PREFIX = '/marketplace/frontend-apis/v2';
const ORDER_API_PREFIX = '/marketplace/order/v2';
const PRODUCT_API_PREFIX = '/marketplace/product/v2';

export async function loadDataManufacturer(ctx = null) {
  const res = await GET_ALL({
    ctx,
    url: `${PRODUCT_API_PREFIX}/manufacturer/list`,
    isBasic: true,
  });
  return getData(res)?.sort((a, b) => (a?.name?.toLowerCase() || '').localeCompare(b?.name?.toLowerCase() || ''));
}

export async function getTabs({ ctx }) {
  return GET({
    ctx,
    url: `${PRODUCT_API_PREFIX}/tabs/active`,
    params: { limit: 100 },
    isBasic: true,
  });
}

export async function getDealsDetail({ ctx, body, params }) {
  return POST({
    ctx,
    url: `${PRODUCT_API_PREFIX}/deal/list`,
    body,
    params,
    isBasic: true,
  });
}

export async function getSettingTags({ ctx, limit = 100 }) {
  return GET_ALL({
    ctx,
    url: `${PRODUCT_API_PREFIX}/tag/list`,
    isBasic: true,
    limit,
  });
}

/**
 * Fetch tags from the API.
 * @param {Object} params - The parameters for the API call.
 * @param {number} [params.offset=0] - The offset for pagination.
 * @param {number} [params.limit=100] - The limit for pagination.
 * @param {string[]} [params.codes=[]] - The list of codes.
 */
export async function getTags({ ctx, offset = 0, limit = 100, ids = [], codes = [], getTotal = false, q }) {
  return GET({
    ctx,
    url: `${PRODUCT_API_PREFIX}/tag/list`,
    params: {
      offset,
      limit: codes.length > 0 ? (ids.length > 0 ? ids.length : codes.length) : limit,
      q: q ? JSON.stringify(q) : null,
      ids: ids.join(','),
      codes: codes.join(','),
      getTotal,
    },
  });
}

export async function getProducts({ ctx, codes, limit, customerLevel, locationCode, getPrice = true, customerId }) {
  return POST({
    ctx,
    url: `${PRODUCT_API_PREFIX}/search/list`,
    body: { skus: codes, getProduct: true, locationCode, customerLevel, getPrice, customerId },
    params: { limit },
  });
}

export async function getProductsByIds({ ctx, ids, ...restProps }) {
  return GET({
    ctx,
    url: `${PRODUCT_API_PREFIX}/product/list`,
    params: { ids },
    ...restProps,
  });
}

export async function getProductsByCodes({ ctx, codes = [], ...restProps }) {
  return POST({
    ctx,
    url: `${PRODUCT_API_PREFIX}/product/list`,
    body: { codes },
    ...restProps,
  });
}

export async function getDescriptionById({ ctx, productId, ...restProps }) {
  return GET({
    ctx,
    url: `${PRODUCT_API_PREFIX}/product/description`,
    params: { q: JSON.stringify({ productID: productId }) },
    ...restProps,
  });
}

export async function getDescriptionBySku({ ctx, sku }) {
  return GET({
    ctx,
    url: `${PRODUCT_API_PREFIX}/product/description`,
    params: {
      q: JSON.stringify({
        code: sku.split('.')[1] || '',
      }),
    },
  });
}

export async function getSkusLimit({ ctx, skuCodes }) {
  return POST({
    ctx,
    url: `${PRODUCT_API_PREFIX}/sku-limit/list`,
    body: {
      skuCodes,
    },
  });
}

export async function getSkusByProductId({ ctx, productIds }) {
  return GET({
    ctx,
    url: `${PRODUCT_API_PREFIX}/sku-item/list`,
    params: {
      productIds,
    },
    isBasic: true,
    isAuth: false,
  });
}

export async function getFuzzySearchClient({ body, ctx, ...restProps }) {
  if (body && body.extraTag && body.filter && body.filter.tag) {
    body.filter.tags = [body.extraTag, body.filter.tag];
    delete body.filter.tag;
  } else if (body.extraTag) {
    body.filter.tag = body.extraTag;
  }
  const { text } = body || {};
  if (text) {
    const textSearch = text.toLocaleLowerCase().trim();
    const lengthTextSearch = textSearch?.split(' ').length;
    if (
      (lengthTextSearch === 1 && PreventSearchKeywords.indexOf(textSearch) >= 0) ||
      textSearch.toLocaleLowerCase().startsWith('http') ||
      PreventSearchKeywordsAlias.indexOf(changeAlias(textSearch)) >= 0
    ) {
      return { status: HTTP_STATUS.NotFound, msg: 'Keyword is not valid' };
    }
  }
  if (typeof body.isAvailable === 'string') {
    body.isAvailable = body.isAvailable === 'true';
  }

  const urlSearch = IS_ENCRYPTED_PRICE ? `${FRONTEND_APIS_PREFIX}/search/fuzzy-encrypted` : `${FRONTEND_APIS_PREFIX}/search/fuzzy`;

  if (restProps.v2) {
    delete restProps.v2;
    body.queryOption = {
      ...body.queryOption,
      consumedMaxQuantity: true,
      sellerInfo: true,
      isGetSalePriceLabel: restProps.isGetSalePriceLabel || false,
      quantityInCart: true,
      getWishList: true,
      getShadowSKU: !!restProps.isQuickOrder,
      isReplacePriceAfterVoucher: true,
      isGetSKUReplace: true,
    };
    //
    if (body.filter?.categoryFilters?.length) {
      for (let i = 0; i < body.filter.categoryFilters.length; i += 1) {
        if (body.filter.categoryFilters[i].efficacyCodes?.length) {
          body.filter.categoryFilters[i].categoryCodes = [];
          body.filter.categoryFilters[i].code = '';
        }
      }
    }
    //
    return POST({
      ctx,
      url: urlSearch,
      body,
      ...restProps,
    });
  }

  // if (IS_ENCRYPTED_PRICE) {
  body.queryOption = {
    ...body.queryOption,
    // isRawFuzzy: true,
    quantityInCart: true,
    getWishList: true,
    isReplacePriceAfterVoucher: true,
  };
  // }
  return POST({
    ctx,
    url: urlSearch,
    body,
    ...restProps,
  });
}

export async function getProductListByVoucher({ filter, offset = 0, limit = 200, text = '', queryOption = {} }) {
  return POST({
    url: `${FRONTEND_APIS_PREFIX}/screen/product/list`,
    body: {
      offset,
      limit,
      filter,
      text,
      isAvailable: true,
      queryOption,
    },
  });
}

export async function getDataProductList({ body, ctx, ...restProps }) {
  if (body?.extraTag && body?.filter?.tag) {
    body.filter.tags = [body.extraTag, body.filter.tag];
    delete body.filter.tag;
  } else if (body?.extraTag) {
    body.filter.tag = body.extraTag;
  }
  const { text } = body || {};
  if (text) {
    const textSearch = text.toLocaleLowerCase().trim();
    const lengthTextSearch = textSearch?.split(' ').length;
    if (
      (lengthTextSearch === 1 && PreventSearchKeywords.indexOf(textSearch) >= 0) ||
      textSearch.toLocaleLowerCase().startsWith('http') ||
      PreventSearchKeywordsAlias.indexOf(changeAlias(textSearch)) >= 0
    ) {
      return { status: HTTP_STATUS.NotFound, msg: 'Keyword is not valid' };
    }
  }
  if (typeof body.isAvailable === 'string') {
    body.isAvailable = body.isAvailable === 'true';
  }

  body.queryOption = {
    ...body.queryOption,
    quantityInCart: true,
    getWishList: true,
    isReplacePriceAfterVoucher: true,
  };

  return POST({
    ctx,
    url: `${FRONTEND_APIS_PREFIX}/screen/product/list`,
    body,
    ...restProps,
  });
}

export async function getFeaturedProductGroups({ productGroups }) {
  return POST({
    url: `${FRONTEND_APIS_PREFIX}/featured-product-group`,
    body: {
      isLite: true,
      limit: 15,
      q: {
        productGroups,
        isReplacePriceAfterVoucher: true,
      },
    },
  });
}

export async function getProductQuickOrder({
  offset = 1,
  text,
  limit,
  isReplacePriceAfterVoucher = false,
  isGetSKUReplace = false,
  quickOrderPage = false,
  isGetCrossSell = false,
}) {
  return POST({
    url: `${FRONTEND_APIS_PREFIX}/quick-order/fuzzy-encrypted`,
    body: {
      offset,
      text,
      limit,
      isReplacePriceAfterVoucher,
      queryOption: {
        isReplacePriceAfterVoucher: true,
        isGetSKUReplace,
        quickOrderPage,
        isGetCrossSell,
      },
    },
  });
}

export async function getCartBySeller(isFistTimeGetCart = false, promosRemoved = '', onSort = true) {
  return GET({
    url: `${FRONTEND_APIS_PREFIX}/screen/cart/info`,
    params: {
      queryOption: `price,consumedMaxQuantity,sellerInfo,isGetSKUReplace,cartPage`,
      getVoucherAuto: !!isFistTimeGetCart,
      redeemCodeRemovedStr: promosRemoved || '',
      onSort,
      // tach SKU uu tien gio hang thanh block seller rieng
      splitCartPriority: true,
    },
  });
}

export async function getBlockList({ ctx, params }) {
  return GET({
    ctx,
    url: `${FRONTEND_APIS_PREFIX}/screen/product/block`,
    params,
  });
}

export async function getReplaceProduct({ offset = 0, limit = 10, slug = '', sku }) {
  return POST({
    url: `${FRONTEND_APIS_PREFIX}/search/substitutes-encrypted`,
    body: sanitize({
      sku,
      offset,
      limit,
      slug,
    }),
  });
}

export default {
  loadDataManufacturer,
  getTabs,
  getSettingTags,
  getDealsDetail,
  getProducts,
  getProductsByIds,
  getTags,
};
